import { useCallback, useEffect, useState } from 'react'
import api from '../utils/api'
import { TimeWindow, TimeWindowFilter } from '@/components/TimeWindowFilter'
import { Button } from '@/components/Button'
import { useToast } from '@/components/Toast'
import { apiErrorMessage } from '@/utils/apiErrors'

interface Approval {
  id: string
  action_type: string
  risk_level: string
  status: string
  action_payload?: Record<string, unknown>
  created_at?: string
  decision_notes?: string
}

export default function BeaconApprovals() {
  const { toast } = useToast()
  const [window, setWindow] = useState<TimeWindow>('month')
  const [rows, setRows] = useState<Approval[]>([])

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/beacon/approvals', { params: { window } })
      setRows(res.data.approvals || [])
    } catch (err: any) {
      toast.error('Failed to load approvals', apiErrorMessage(err))
    }
  }, [window, toast])

  useEffect(() => { load() }, [load])

  const decide = async (id: string, decision: 'approved' | 'rejected') => {
    try {
      await api.post(`/api/v1/beacon/approvals/${id}/decide`, { decision })
      toast.success(decision === 'approved' ? 'Approved' : 'Rejected')
      await load()
    } catch (err: any) {
      toast.error('Decision failed', apiErrorMessage(err))
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-slate-900">Approvals (HITL)</h2>
          <p className="text-sm text-slate-600">Destructive deletes and public exposure require your sign-off.</p>
        </div>
        <TimeWindowFilter value={window} onChange={setWindow} />
      </div>

      <div className="space-y-3">
        {rows.length === 0 && <p className="text-sm text-slate-400">No Beacon approvals in this window.</p>}
        {rows.map((a) => (
          <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-800">{a.risk_level}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5">{a.status}</span>
                </div>
                <h3 className="mt-1 font-medium text-slate-900">{a.action_type}</h3>
                <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
                  {JSON.stringify(a.action_payload || {}, null, 2)}
                </pre>
                {a.created_at && <p className="mt-1 text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</p>}
              </div>
              {a.status === 'pending' && (
                <div className="flex gap-2">
                  <Button onClick={() => decide(a.id, 'approved')}>Approve</Button>
                  <Button variant="secondary" onClick={() => decide(a.id, 'rejected')}>Reject</Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
